import { diffLines, diffWordsWithSpace } from "diff";

/** One piece of a changed line; `changed` marks the words that differ from the paired line. */
export interface DiffSegment {
    text: string;
    changed: boolean;
}

export interface DiffRow {
    type: "add" | "del" | "same";
    oldNo?: number;
    newNo?: number;
    segments: DiffSegment[];
}

/** A run of unchanged lines folded away; `rows` are shown when the reviewer expands it. */
export interface DiffFold {
    type: "fold";
    rows: DiffRow[];
}

export type DiffLine = DiffRow | DiffFold;

export interface LineDiff {
    lines: DiffLine[];
    added: number;
    removed: number;
}

/** Unchanged lines kept around each change. */
const Context = 3;

function splitLines(value: string): string[] {
    const lines = value.split("\n");
    if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    return lines;
}

/** Word-level highlight for a removed line and the added line that replaces it. */
function pairSegments(before: string, after: string): [DiffSegment[], DiffSegment[]] {
    const del: DiffSegment[] = [];
    const add: DiffSegment[] = [];
    for (const part of diffWordsWithSpace(before, after)) {
        if (!part.added) del.push({ text: part.value, changed: !!part.removed });
        if (!part.removed) add.push({ text: part.value, changed: !!part.added });
    }
    return [del, add];
}

/** Line diff of two markdown bodies, unchanged runs longer than the context folded. */
export function lineDiff(before: string, after: string): LineDiff {
    const rows: DiffRow[] = [];
    let oldNo = 0;
    let newNo = 0;
    let added = 0;
    let removed = 0;

    const parts = diffLines(before, after);
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const lines = splitLines(part.value);
        if (part.removed && parts[i + 1]?.added) {
            // a replaced block: pair line by line so the changed words stand out
            const next = splitLines(parts[i + 1].value);
            const pairs = Math.min(lines.length, next.length);
            for (let k = 0; k < lines.length; k++) {
                const segs = k < pairs ? pairSegments(lines[k], next[k])[0] : [{ text: lines[k], changed: false }];
                rows.push({ type: "del", oldNo: ++oldNo, segments: segs });
            }
            for (let k = 0; k < next.length; k++) {
                const segs = k < pairs ? pairSegments(lines[k], next[k])[1] : [{ text: next[k], changed: false }];
                rows.push({ type: "add", newNo: ++newNo, segments: segs });
            }
            removed += lines.length;
            added += next.length;
            i++;
            continue;
        }
        for (const line of lines) {
            if (part.added) {
                rows.push({ type: "add", newNo: ++newNo, segments: [{ text: line, changed: false }] });
                added++;
            } else if (part.removed) {
                rows.push({ type: "del", oldNo: ++oldNo, segments: [{ text: line, changed: false }] });
                removed++;
            } else {
                rows.push({ type: "same", oldNo: ++oldNo, newNo: ++newNo, segments: [{ text: line, changed: false }] });
            }
        }
    }

    // fold unchanged runs, keeping some context next to each change
    const lines: DiffLine[] = [];
    let run: DiffRow[] = [];
    const flush = (atStart: boolean, atEnd: boolean) => {
        const keepBefore = atStart ? 0 : Context;
        const keepAfter = atEnd ? 0 : Context;
        if (run.length > keepBefore + keepAfter + 1) {
            lines.push(...run.slice(0, keepBefore));
            lines.push({ type: "fold", rows: run.slice(keepBefore, run.length - keepAfter) });
            lines.push(...run.slice(run.length - keepAfter));
        } else {
            lines.push(...run);
        }
        run = [];
    };
    rows.forEach((row) => {
        if (row.type === "same") {
            run.push(row);
            return;
        }
        if (run.length) flush(lines.length === 0, false);
        lines.push(row);
    });
    if (run.length) flush(lines.length === 0, true);

    return { lines, added, removed };
}
